<template>
  <div>
    <!-- 如果数据已加载，则显示进度页面，否则显示预加载 -->
    <div v-if="loaded">
      <!-- Replace inline hint with TutorialHint component -->
      <tutorial-hint 
        v-if="showHint"
        :steps="tutorialSteps"
        :autoShow="true"
        @close="dismissHint"
        ref="tutorialHint"
      />
      
      <!-- 进度页面 -->
      <div v-if="!inCustomization"  class="main-page">
        
        <div v-if="profileType==='memorialize'">
          <star-animation
            :quoteText="'Love is a shooting star that lands in your heart and lives forever'" 
            :numStars="personalizationSets.length"
            :authorText="'DANIELLE STEEL'"
            :startCustomization="startCustomization"
            :personalizationSet="personalizationSets"
            :nextStep="endCustomization"
            :nextPage="nextPage"
          >
          </star-animation>
        </div>
        <div v-else-if="profileType==='temporary-separation'">
          <away-home
            :prodName="prodName"
            :quote="'Missing you comes in waves. Tonight I\'m swimming'"
            :author="'UNKNOWN'"
            :personalizationSet="personalizationSets"
            :startCustomization="startCustomization"
            :nextStep="endCustomization"
            :nextPage="nextPage"
          />
        </div>
        <div v-else-if="profileType==='animal-love'">
          <wild
            :prodName="prodName"
            :quote="'In your wild eyes, I found my story'"
            :author="'UNKNOWN'"
            :personalizationSet="personalizationSets"
            :startCustomization="startCustomization"
            :nextStep="endCustomization"
            :nextPage="nextPage"
          />
        </div>
        <div v-else-if="profileType==='love'">
          <heart-animation
            :prodName="prodName"
            :quote="'Love is a shooting star that lands in your heart, and lives forever.'"
            :author="'UNKNOWN'"
            :personalizationSet="personalizationSets"
            :startCustomization="startCustomization"
            :nextStep="endCustomization"
            :nextPage="nextPage"
          />
        </div>
        <div v-else>
          <gifting 
            :prodName="prodName"
            :quote="`A gift from the heart touches the soul`"
            :author="'UNKNOWN'"
            :personalizationSet="personalizationSets"
            :startCustomization="startCustomization"
            :nextStep="endCustomization"
            :nextPage="nextPage"
          />
        </div>
        
      </div>

      <!-- 定制页面 -->
      <div v-else>
        <custom-level-card
          v-if="currentIndex !== null && configurationType === '' && personalizationSets[currentIndex] && personalizationSets[currentIndex].components && personalizationSets[currentIndex].components.length > 0"
          :setConfigurationType="setConfigurationType"
          :productName="personalizationSets[currentIndex].name"
          :showed_image_amount="personalizationSets[currentIndex].currentComponent?.props?.showed_image_amount || 120"
        />
        <component
          v-if="currentIndex !== null && configurationType === 'simple' && personalizationSets[currentIndex] && personalizationSets[currentIndex].components && personalizationSets[currentIndex].components.length > 0 && personalizationSets[currentIndex].currentComponent"
          :is="componentMap[personalizationSets[currentIndex].currentComponent.id]"
          v-bind="personalizationSets[currentIndex].currentComponent.props || {}"
          @updateSelection="handleSelection"
          @next="finishCustomization"
          @cancel="cancelCustomization"
        />
        <component
          v-if="currentIndex !== null && configurationType === 'custom' && personalizationSets[currentIndex] && personalizationSets[currentIndex].components && personalizationSets[currentIndex].components.length > 0"
          :is="'SimpleConfigurationComponent'"
          @next="finishCustomization"
        />
        <!-- Feature under development message -->
        <div v-if="currentIndex !== null && configurationType === 'custom'" class="under-development">
          <div class="development-message">
            <h3>This feature is still under development</h3>
            <p>We're working hard to bring you the best customization experience. Please check back soon!</p>
            <button @click="cancelCustomization" class="back-button">Go Back</button>
          </div>
        </div>
      </div>

      <!-- Help button to review instructions -->
      <button v-if="!inCustomization" class="help-button" @click="showTutorial" title="Review Instructions">
        ?
      </button>
    </div>

    <!-- 未加载时，显示预加载组件 -->
    <product-preloader v-else>Loading…</product-preloader>
  </div>
</template>

<script>
import PersonalizationButton from './PersonalizationButton.vue';
import ImageSelector from './ImageSelector.vue';
import ColorSelector from './ColorSelector.vue';
import ProductPreloader from '@/components/preloaders/ProductPreloader.vue';
import StarAnimation from '@/components/animations/StarAnimation.vue';
import AwayHome from '@/components/animations/AwayHome.vue';
import Gifting from '@/components/animations/Gifting.vue';
import Wild from '@/components/animations/Wild.vue';
import CustomLevelCard from '../cards/CustomLevelCard.vue';
import QuoteInputor from './QuoteInputor.vue';
import NoCustomizationNeeded from './NoCustomizationNeeded.vue';
import HeartAnimation from '@/components/animations/HeartAnimation.vue';
import TutorialHint from './TutorialHint.vue';
import ColorSpectrumSelector from './ColorSpectrumSelector.vue';
import PhoneCaseModelSelector from './PhoneCaseModelSelector.vue';
import { uploadImagesForItem, processAndUploadItemImages, createPicturesPersonalization, createTextPersonalization, createColorPersonalization, createSelectionPersonalization } from '@/services/itemImageUploadService';
import { auth } from '@/firebase/init';
import { markRaw } from 'vue';

export default {
  name: 'FullControl',
  components: {
    PersonalizationButton,
    ImageSelector,
    ColorSelector,
    QuoteInputor,
    NoCustomizationNeeded,
    ColorSpectrumSelector,
    PhoneCaseModelSelector,
    ProductPreloader,
    StarAnimation,
    AwayHome,
    Gifting,
    CustomLevelCard,
    Wild,
    HeartAnimation,
    TutorialHint
  },
  props: {
    profileType: {
      type: String,
      // default: 'gifting',
      required: true
    },
    images: {
      type: Array,
      default: () => []
    },
    prodName: {
      type: String,
      default: 'Everlast'
    },
    initialSets: {
      type: Array,
      default: () => null
    },
    initialState: {
      type: Object,
      default: () => null
    },
    journeyId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loaded: false,            // 数据是否加载完成
      inCustomization: false,   // 是否处于定制页面
      currentIndex: null,       // 当前定制的产品索引
      componentMap: markRaw({   // 组件映射表 - use markRaw to prevent reactivity warning
        PersonalizationButton,
        ImageSelector,
        ColorSelector,
        QuoteInputor,
        NoCustomizationNeeded,
        ColorSpectrumSelector,
        PhoneCaseModelSelector
      }),
      personalizationSets: [],   // 产品列表，每个产品包含定制信息
      configurationType: '',
      customizationStep: 0,
      showHint: true,            // Controls visibility of the personalization hint
      tutorialSteps: [
        {
          img: '/assets/images/tutorial/1.png',
          title: 'Select a product',
          description: 'Select a product to customize'
        },
        {
          img: '/assets/images/tutorial/2.png',
          title: 'Customize',
          description: 'Customize product with preferred method'
        },
        {
          img: '/assets/images/tutorial/3.png',
          title: 'Repeat',
          description: 'Repeat process for all products'
        }
      ]
    };
  },
  mounted() {
    this.fetchPersonalizationSets();
    // Check if the hint has been shown before
    const hintShown = localStorage.getItem('personalizationHintShown');
    if (hintShown === 'true') {
      this.showHint = false;
    }
  },
  methods: {
    persistProgress() {
      try {
        // STEP 1: Check what we're about to emit
        console.log('🔄 STEP 1 - persistProgress() called');
        console.log('   personalizationSets:', this.personalizationSets);
        
        const productsWithData = this.personalizationSets.filter(s => s.personalization?.length > 0);
        console.log(`   Emitting ${productsWithData.length}/${this.personalizationSets.length} products with personalization`);
        
        // Log each product's personalization
        this.personalizationSets.forEach((set, idx) => {
          if (set.personalization && set.personalization.length > 0) {
            console.log(`   ✅ Product ${idx} (${set.name}): ${set.personalization.length} entries`);
            set.personalization.forEach(entry => {
              if (entry.type === 'imagePersonalization') {
                console.log(`      - ${entry.type}: ${entry.input?.length || 0} inputs`);
                entry.input?.forEach((inputItem, idx) => {
                  if (inputItem.type === 'uploaded') {
                    console.log(`        [${idx}] uploaded: ${inputItem.urls?.length || 0} images`);
                  } else if (inputItem.type === 'social') {
                    console.log(`        [${idx}] social: ${inputItem.platform || 'none'} @${inputItem.username || 'none'}`);
                  }
                });
              } else if (entry.type === 'pictures') {
                const urls = entry.input?.urls || entry.input || [];
                console.log(`      - ${entry.type}: ${urls.length} images (legacy)`);
              } else if (entry.type === 'socialMedia') {
                const platform = entry.input?.platform || 'none';
                const username = entry.input?.username || 'none';
                console.log(`      - ${entry.type}: ${platform} @${username} (legacy)`);
              } else {
                console.log(`      - ${entry.type}: ${entry.input}`);
              }
            });
          } else {
            console.log(`   ⚪ Product ${idx} (${set.name}): no personalization`);
          }
        });
        
        this.$emit('persistSets', this.personalizationSets);
        this.$emit('persistState', {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
          // Additional state for F5 refresh restoration
          currentProductName: this.currentIndex !== null ? this.personalizationSets[this.currentIndex]?.name : null,
          timestamp: Date.now()
        });
      } catch (e) {
        console.error('persistProgress failed', e);
      }
    },
    /**
     * 获取产品定制数据
     * 
     * DATA SEPARATION ARCHITECTURE:
     * =============================
     * 
     * 1. JSON FILE (sets2.json) - COMPONENT STRUCTURE:
     *    - Defines what inputs to show (ImageSelector, ColorSelector, TextInput, etc.)
     *    - Contains component props, validation rules, UI structure
     *    - This is the "template" - never modified by user
     * 
     * 2. USER DATA - ACTUAL INPUT VALUES:
     *    Saved in TWO formats for compatibility:
     * 
     *    a) NEW FORMAT (personalization array):
     *       product.personalization = [
     *         { type: 'pictures', input: [url1, url2], timestamp: 123 },
     *         { type: 'text', input: 'lorem ipsum', timestamp: 124 },
     *         { type: 'color', input: '#FF5733', timestamp: 125 }
     *       ]
     * 
     *    b) OLD FORMAT (for backward compatibility):
     *       product.userUploadedImageUrls = [url1, url2]
     *       product.userSelections = { text: 'lorem', color: '#FF5733' }
     * 
     * 3. DATA FLOW:
     *    Load: JSON structure + Saved user data → Merged product
     *    Save: User input → personalization array → Cookies + Firebase
     *    Display: personalization array → userSelections → Component display
     */
     async fetchPersonalizationSets() {
        this.loaded = false;
        fetch("/data/personalization/sets2.json")
                .then((response) => response.json())
                .then((data) => {
                    this.personalizationSets = data[this.prodName].products.map((product, index) => {
                        product.showComponent = index === 0 ? true : false;
                        
                        // Ensure isComplete field exists
                        if (product.isComplete === undefined) {
                          product.isComplete = false;
                        }
                        
                        for (let i = 0; i < product.components.length; i++) {
                            if (product.components[i].id === "ImageSelector") {
                                product.components[i].props.imagesList = this.images
                            }
                            // product.components[i].emits = {};
                        }
                        if (product.components.length > 0)
                            product.currentComponent = product.components[0];
                        else
                            product.isComplete = true;
                        this.loaded = true;
                        return product;
                    });
                    
                    // Restore user data from initialSets if provided
                    // IMPORTANT: Keep JSON structure (components), only restore USER INPUT
                    if (this.initialSets && Array.isArray(this.initialSets)) {
                      try {
                        this.personalizationSets = this.personalizationSets.map((freshProduct, index) => {
                          const savedProduct = this.initialSets[index];
                          
                          if (!savedProduct) {
                            return freshProduct;
                          }
                          
                          // SEPARATION OF CONCERNS:
                          // 1. JSON FILE = Component structure (what inputs to show)
                          // 2. USER DATA = Actual values entered by user
                          
                          const mergedProduct = {
                            ...freshProduct, // Keep ALL structure from JSON (components, props, etc.)
                            
                            // USER COMPLETION STATUS
                            isComplete: savedProduct.isComplete || false,
                            showComponent: savedProduct.showComponent !== undefined ? savedProduct.showComponent : freshProduct.showComponent,
                            
                            // USER INPUT DATA - personalization array (new format)
                            personalization: savedProduct.personalization || [],
                            personalizationType: savedProduct.personalizationType || null
                            // Note: userUploadedImageUrls and userSelections are deprecated
                          };
                          
                          // If personalization array exists, restore user input from it
                          if (savedProduct.personalization && savedProduct.personalization.length > 0) {
                            this.restoreUserInputToComponents(mergedProduct);
                          }
                          
                          return mergedProduct;
                        });
                        
                        console.log('Restored user data into fresh component structure');
                      } catch (err) {
                        console.error('Error restoring user data:', err);
                      }
                    }
                    if (this.initialState) {
                      const s = this.initialState;
                      this.inCustomization = !!s.inCustomization;
                      this.currentIndex = typeof s.currentIndex === 'number' ? s.currentIndex : null;
                      this.configurationType = s.configurationType || '';
                      this.customizationStep = typeof s.customizationStep === 'number' ? s.customizationStep : 0;
                    }
                    // emit a first persist to ensure parent has latest
                    this.persistProgress();
                    console.log(this.personalizationSets);
                })
                .catch((err) => {
                    console.log(err);
                    this.loaded = false;
                });
      },
    /**
     * 开始定制某个产品
     * @param {Number} index - 产品索引
     */
    startCustomization(index) {
      // Safety check: ensure personalizationSets and the item exist
      if (!this.personalizationSets || !Array.isArray(this.personalizationSets)) {
        console.error('personalizationSets is not initialized');
        return;
      }
      
      if (!this.personalizationSets[index]) {
        console.error(`Product at index ${index} does not exist`);
        return;
      }
      
      const product = this.personalizationSets[index];
      
      // Check if product has components
      if (!product.components || !Array.isArray(product.components)) {
        console.error(`Product at index ${index} has no components array`);
        alert('This product is not customizable.');
        return;
      }
      
      if(product.components.length === 0) {
        alert('This product is not customizable.');
        return;
      }
      
      this.currentIndex = index;
      
      // Restore any previously saved inputs for this product
      this.restoreUserInputToComponents(product);
      
      // 如果产品只能专业化，直接跳过选择页面
      if(product.professional === true){
        this.setConfigurationType('simple');
      }
      this.inCustomization = true;
      // 确保只有一个产品在定制中
      // 可选：滚动到顶部
      window.scrollTo(0, 0);
      this.persistProgress();
    },

    setConfigurationType(type) {
      this.configurationType = type;
      
      // Set personalizationType based on user's choice
      if (this.currentIndex !== null && this.personalizationSets[this.currentIndex]) {
        const currentProduct = this.personalizationSets[this.currentIndex];
        
        if (type === 'simple') {
          // User chose "Personalize Themselves" (custom)
          currentProduct.personalizationType = 'custom';
          console.log(`${currentProduct.name}: User chose CUSTOM personalization`);
        } else if (type === 'professional') {
          // User chose "Fast Personalization" (professional)
          currentProduct.personalizationType = 'professional';
          console.log(`${currentProduct.name}: User chose PROFESSIONAL personalization`);
        }
      }
      
      this.persistProgress();
    },

    /**
     * 完成定制后，更新产品状态并返回进度页面
     */
    finishCustomization() {
      // Safety checks
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot finish customization: invalid state');
        return;
      }
      
      const currentProduct = this.personalizationSets[this.currentIndex];
      
      if (!currentProduct.components || !Array.isArray(currentProduct.components)) {
        console.error('Cannot finish customization: no components array');
        return;
      }
      
      if (this.customizationStep >= currentProduct.components.length - 1) {
        currentProduct.isComplete = true;
        this.customizationStep = 0;
        currentProduct.currentComponent = currentProduct.components[0];
        this.inCustomization = false;
        this.currentIndex = null;
        // 返回顶部
        window.scrollTo(0, 0);
        this.setConfigurationType('');
        // 可选择在此处触发事件或调用 API 保存定制结果
        // this.$emit('updateProduct', this.personalizationSets);
      } else {
        this.customizationStep++;
        currentProduct.currentComponent = currentProduct.components[this.customizationStep];
        if (currentProduct.currentComponent && currentProduct.currentComponent.id === "ImageSelector") {
          currentProduct.currentComponent.props = {
            title: "Select Image",
            imagesList: this.images,
            maxSelect: 1
          };
        }
      }
      this.persistProgress();
      // // aaaaaa
      // if (this.currentIndex !== null) {
      //   this.personalizationSets[this.currentIndex].isComplete = true;
      // }
      // this.inCustomization = false;
      // this.currentIndex = null;
      // // 返回顶部
      // window.scrollTo(0, 0);
      // this.setConfigurationType('');
      // // 可选择在此处触发事件或调用 API 保存定制结果
      // // this.$emit('updateProduct', this.personalizationSets);
    },
    endCustomization() {
      for (let i = 0; i < this.personalizationSets.length; i++) {
        if (!this.personalizationSets[i].isComplete) {
          alert('Please complete all products before finishing.');
          return false;
        }
      }
      return true;
    },
    nextPage(){
      this.$emit('updateProduct', this.personalizationSets);
    },

    /**
     * 取消定制，返回进度页面
     */
    cancelCustomization() {
      this.inCustomization = false;
      
      this.customizationStep = 0;
      
      // Safety check before accessing personalizationSets
      if (this.currentIndex !== null && this.personalizationSets && this.personalizationSets[this.currentIndex]) {
        const currentProduct = this.personalizationSets[this.currentIndex];
        if (currentProduct.components && Array.isArray(currentProduct.components) && currentProduct.components.length > 0) {
          currentProduct.currentComponent = currentProduct.components[0];
        }
      }
      
      // 返回顶部
      this.currentIndex = null;
      window.scrollTo(0, 0);
      this.setConfigurationType('');
      this.persistProgress();
    },

    /**
     * 处理子组件的选择更新
     * @param {Object} selection - 选择数据
     */
    async handleSelection(selection) {
      console.log('选择更新:', selection);
      
      // Safety checks
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      
      if (!currentSet.currentComponent) {
        console.error('Cannot handle selection: no current component');
        return;
      }
      
      const currentComponent = currentSet.currentComponent;
      
      // Store selection in component emits
      if (!currentComponent.emits) {
        currentComponent.emits = {};
      }
      currentComponent.emits.selection = selection;
      
      // Handle ImageSelector component (both uploaded images and social media)
      if (currentComponent.id === 'ImageSelector' && selection && typeof selection === 'object') {
        // Check if it's uploaded images or social media
        if (selection.type === 'uploaded' || selection.type === 'social') {
          console.log('🖼️ ImageSelector detected, calling handleImageSelection with:', selection);
          await this.handleImageSelection(selection, currentSet.name);
        }
      }
      
      // Handle text input - QuoteInputor emits the text directly as a string
      if (currentComponent.id === 'QuoteInputor' && typeof selection === 'string') {
        console.log('📝 QuoteInputor detected, calling handleTextSelection with:', selection);
        this.handleTextSelection(selection, currentSet.name);
      }
      
      // Handle color selection
      // ColorSelector emits an array: ["#FF5733"]
      // ColorSpectrumSelector emits a string: "#E6F4EA"
      if (currentComponent.id === 'ColorSelector' && Array.isArray(selection)) {
        console.log('🎨 ColorSelector detected, calling handleColorSelection with:', selection);
        const color = selection[0];
        if (color) {
          this.handleColorSelection(color, currentSet.name);
        }
      } else if (currentComponent.id === 'ColorSpectrumSelector' && typeof selection === 'string' && selection.startsWith('#')) {
        console.log('🎨 ColorSpectrumSelector detected, calling handleColorSelection with:', selection);
        this.handleColorSelection(selection, currentSet.name);
      }
      
      // Handle phone model selection - PhoneCaseModelSelector emits object: {brand, model}
      if (currentComponent.id === 'PhoneCaseModelSelector' && selection && selection.brand && selection.model) {
        console.log('📱 PhoneCaseModelSelector detected, calling handlePhoneModelSelection with:', selection);
        this.handlePhoneModelSelection(selection, currentSet.name);
      }
      
      this.persistProgress();
    },
    
    /**
     * Handle image selection from ImageSelector component
     * Uploads images to Firebase and saves URLs
     */
    async handleImageSelection(selection, itemName) {
      try {
        console.log('📸 handleImageSelection called for:', itemName);
        console.log('   Selection data:', selection);
        
        if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
          console.error('Cannot handle image selection: invalid state');
          return;
        }
        
        const currentSet = this.personalizationSets[this.currentIndex];
        
        // Store in personalization array
        if (!currentSet.personalization) {
          currentSet.personalization = [];
        }
        
        // Find or create imagePersonalization entry (merges pictures and social media)
        let imagePersonalizationIndex = currentSet.personalization.findIndex(p => p.type === 'imagePersonalization');
        
        if (imagePersonalizationIndex < 0) {
          // Create new imagePersonalization entry
          currentSet.personalization.push({
            type: 'imagePersonalization',
            input: [],
            timestamp: Date.now()
          });
          imagePersonalizationIndex = currentSet.personalization.length - 1;
          console.log('   ✅ Created new imagePersonalization entry');
        }
        
        const imagePersonalization = currentSet.personalization[imagePersonalizationIndex];
        
        // Handle different selection types
        if (selection.type === 'social') {
          // Social media selection
          console.log('   📱 Social media selection detected');
          
          const socialInput = {
            type: 'social',
            platform: selection.platform || null,
            username: selection.username || null,
            multiplePets: selection.multiplePets || false,
            multiplePetsList: selection.multiplePetsList || [],
            portraitStyle: selection.portraitStyle || false,
            disclaimerData: selection.disclaimerData || null,
            selectedOutfitName: selection.selectedOutfitName || null,
            selectedOutfitStyle: selection.selectedOutfitStyle || null,
            timestamp: Date.now()
          };
          
          // Find existing social entry in the input array
          const existingSocialIndex = imagePersonalization.input.findIndex(i => i.type === 'social');
          
          if (existingSocialIndex >= 0) {
            imagePersonalization.input[existingSocialIndex] = socialInput;
            console.log('   ✅ Updated social media in imagePersonalization:', socialInput);
          } else {
            imagePersonalization.input.push(socialInput);
            console.log('   ✅ Added social media to imagePersonalization:', socialInput);
          }
        } else if (selection.type === 'uploaded') {
          // Uploaded images
          console.log('   📤 Uploaded images selection detected');
          
          const existingUploadedIndex = imagePersonalization.input.findIndex(i => i.type === 'uploaded');
          const existingUploadedEntry = existingUploadedIndex >= 0 ? imagePersonalization.input[existingUploadedIndex] : null;
          
          const uploadedInput = {
            type: 'uploaded',
            urls: existingUploadedEntry?.urls ? [...existingUploadedEntry.urls] : [],
            multiplePets: selection.multiplePets || false,
            multiplePetsList: selection.multiplePetsList || [],
            portraitStyle: selection.portraitStyle || false,
            selectedOutfitName: selection.selectedOutfitName || null,
            selectedOutfitStyle: selection.selectedOutfitStyle || null,
            timestamp: Date.now()
          };
          
          let imageUrls = [];
          const hasPendingFiles = selection.images?.some(img => img.file);
          
          if (hasPendingFiles) {
            console.log('   Processing images for upload...');
            const userId = auth.currentUser?.uid || this.$store.state.user?.uid;
            const journeyId = this.journeyId || this.prodName;
            
            console.log('   Upload params:', {
              userId: userId,
              journeyId: journeyId,
              itemName: itemName
            });
            
            if (!userId) {
              console.error('   ❌ No user ID available - user not logged in');
              throw new Error('User must be logged in to upload images');
            }
            
            // Path: user/{userId}/journey/{journeyId}/item/{itemName}/image-X.jpeg
            // journeyId = productId (e.g., 621b7028fd494b93bb109624-1766233682253)
            // itemName = product name (e.g., Crystal_Carving, Lightbox)
            imageUrls = await processAndUploadItemImages(
              selection.images,
              journeyId,
              itemName
            );
            console.log('   ✅ Images uploaded, URLs:', imageUrls);
          } else if (selection.images && selection.images.length > 0) {
            imageUrls = selection.images
              .map(img => img.url)
              .filter(url => typeof url === 'string' && url.length > 0);
          }
          
          if (imageUrls.length > 0) {
            uploadedInput.urls = imageUrls;
          }
          
          if (!uploadedInput.urls || uploadedInput.urls.length === 0) {
            const fallbackUrls = selection.images
              ?.map(img => img.url)
              .filter(url => typeof url === 'string' && url.length > 0);
            if (fallbackUrls && fallbackUrls.length > 0) {
              uploadedInput.urls = fallbackUrls;
            }
          }
          
          if (existingUploadedIndex >= 0) {
            imagePersonalization.input[existingUploadedIndex] = uploadedInput;
            console.log('   ✅ Updated uploaded images in imagePersonalization');
          } else {
            imagePersonalization.input.push(uploadedInput);
            console.log('   ✅ Added uploaded images to imagePersonalization');
          }
        }
        
        console.log('   📦 Final personalization array:', currentSet.personalization);
        
        // Persist to parent
        this.persistProgress();
      } catch (error) {
        console.error('Error in handleImageSelection:', error);
      }
    },
    
    /**
     * Handle text selection
     */
    handleTextSelection(text, itemName) {
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle text selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      
      // Store in personalization array
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Check if text entry already exists
      const textIndex = currentSet.personalization.findIndex(p => p.type === 'text');
      const textEntry = {
        type: 'text',
        input: text,
        timestamp: Date.now()
      };
      
      if (textIndex >= 0) {
        currentSet.personalization[textIndex] = textEntry;
      } else {
        currentSet.personalization.push(textEntry);
      }
      
      console.log(`Saved text for ${itemName}:`, text);
      console.log('Saved to personalization array:', textEntry);
      
      // Persist to parent
      this.persistProgress();
    },
    
    /**
     * Handle color selection
     */
    handleColorSelection(color, itemName) {
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle color selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      
      // Store in personalization array
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Check if color entry already exists
      const colorIndex = currentSet.personalization.findIndex(p => p.type === 'color');
      const colorEntry = {
        type: 'color',
        input: color,
        timestamp: Date.now()
      };
      
      if (colorIndex >= 0) {
        currentSet.personalization[colorIndex] = colorEntry;
      } else {
        currentSet.personalization.push(colorEntry);
      }
      
      console.log(`选择更新: ${color}`);
      console.log('Saved to personalization array:', colorEntry);
      
      // Persist to parent
      this.persistProgress();
    },
    
    /**
     * Handle phone model selection
     */
    handlePhoneModelSelection(modelData, itemName) {
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle phone model selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      
      // Store in personalization array
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Check if phoneModel entry already exists
      const phoneModelIndex = currentSet.personalization.findIndex(p => p.type === 'phoneModel');
      const phoneModelEntry = {
        type: 'phoneModel',
        input: modelData, // {brand: "iPhone", model: "iPhone 15 Pro"}
        timestamp: Date.now()
      };
      
      if (phoneModelIndex >= 0) {
        currentSet.personalization[phoneModelIndex] = phoneModelEntry;
      } else {
        currentSet.personalization.push(phoneModelEntry);
      }
      
      console.log(`Saved phone model for ${itemName}:`, modelData);
      console.log('Saved to personalization array:', phoneModelEntry);
      
      // Persist to parent
      this.persistProgress();
    },
    
    dismissHint() {
      this.showHint = false;
      // Optionally save to localStorage to make it persistent across sessions
      localStorage.setItem('personalizationHintShown', 'true');
    },
    
    showTutorial() {
      this.showHint = true;
      // If the component already exists, we might need to call open() 
      // but since we use v-if="showHint", setting it to true will remount 
      // and onMounted will call open() automatically.
    },
    
    /**
     * Restore user input from personalization array
     * This populates the component display with saved user data
     */
    restoreUserInputToComponents(product) {
      if (!product.personalization || !Array.isArray(product.personalization)) {
        console.log('No personalization data to restore for:', product.name);
        return;
      }
      
      console.log(`Restoring personalization for ${product.name}:`, product.personalization);
      console.log(`Personalization type: ${product.personalizationType || 'not set'}`);
      
      // Iterate through each personalization entry
      product.personalization.forEach(entry => {
        switch (entry.type) {
          case 'imagePersonalization':
            // New merged structure with array of inputs
            console.log(`  - Restoring imagePersonalization with ${entry.input?.length || 0} inputs`);
            
            if (entry.input && Array.isArray(entry.input)) {
              entry.input.forEach(inputItem => {
                if (inputItem.type === 'uploaded' && inputItem.urls && inputItem.urls.length > 0) {
                  // Restore uploaded images
                  console.log(`    - Uploaded: ${inputItem.urls.length} images`);
                  
                  const imageSelectorComponent = product.components?.find(c => 
                    (typeof c === 'string' && c === 'ImageSelector') || 
                    (c && c.id === 'ImageSelector')
                  );
                  
                  if (imageSelectorComponent && typeof imageSelectorComponent === 'object') {
                    if (!imageSelectorComponent.props) {
                      imageSelectorComponent.props = {};
                    }
                    imageSelectorComponent.props.preSelectedImages = inputItem.urls.map(url => ({ url }));
                    
                    if (inputItem.multiplePets || inputItem.multiplePetsList?.length > 0 || inputItem.portraitStyle !== undefined) {
                      imageSelectorComponent.props.preSelectedMultiplePets = {
                        multiplePets: inputItem.multiplePets,
                        multiplePetsList: inputItem.multiplePetsList,
                        portraitStyle: inputItem.portraitStyle
                      };
                    }
                    if (inputItem.selectedOutfitName || inputItem.selectedOutfitStyle) {
                      imageSelectorComponent.props.preSelectedOutfit = {
                        selectedOutfitName: inputItem.selectedOutfitName || null,
                        selectedOutfitStyle: inputItem.selectedOutfitStyle || null,
                        portraitStyle: inputItem.portraitStyle ?? null
                      };
                    }
                    console.log(`      ✅ Set preSelectedImages prop`);
                  }
                } else if (inputItem.type === 'social') {
                  // Restore social media
                  const username = inputItem.username || '';
                  const platform = inputItem.platform || '';
                  console.log(`    - Social: ${platform} @${username || '(empty)'}`);
                  
                  const imageSelectorComponent = product.components?.find(c => 
                    (typeof c === 'string' && c === 'ImageSelector') || 
                    (c && c.id === 'ImageSelector')
                  );
                  
                  if (imageSelectorComponent && typeof imageSelectorComponent === 'object') {
                    if (!imageSelectorComponent.props) {
                      imageSelectorComponent.props = {};
                    }
                    imageSelectorComponent.props.preSelectedSocialMedia = {
                      platform: inputItem.platform || null,
                      username: inputItem.username || null,
                      multiplePets: inputItem.multiplePets || false,
                      multiplePetsList: inputItem.multiplePetsList || [],
                      portraitStyle: inputItem.portraitStyle || false
                    };
                    if (inputItem.selectedOutfitName || inputItem.selectedOutfitStyle) {
                      imageSelectorComponent.props.preSelectedOutfit = {
                        selectedOutfitName: inputItem.selectedOutfitName || null,
                        selectedOutfitStyle: inputItem.selectedOutfitStyle || null,
                        portraitStyle: inputItem.portraitStyle ?? null
                      };
                    }
                    console.log(`      ✅ Set preSelectedSocialMedia prop`);
                  }
                }
              });
            }
            break;
            
          case 'pictures':
            // Restore images - entry.input can be array of URLs (old format) or object with urls + metadata (new format)
            let imageUrls = [];
            let metadata = {};
            let outfitMetadata = null;
            
            if (Array.isArray(entry.input)) {
              // Old format: just array of URLs
              imageUrls = entry.input;
            } else if (entry.input && entry.input.urls) {
              // New format: object with urls and metadata
              imageUrls = entry.input.urls;
              metadata = {
                multiplePets: entry.input.multiplePets,
                multiplePetsList: entry.input.multiplePetsList,
                portraitStyle: entry.input.portraitStyle
              };
              outfitMetadata = {
                selectedOutfitName: entry.input.selectedOutfitName,
                selectedOutfitStyle: entry.input.selectedOutfitStyle,
                portraitStyle: entry.input.portraitStyle
              };
            }
            
            if (imageUrls.length > 0) {
              console.log(`  - Restoring ${imageUrls.length} images with metadata:`, metadata);
              
              // Find the ImageSelector component
              const imageSelectorComponent = product.components?.find(c => 
                (typeof c === 'string' && c === 'ImageSelector') || 
                (c && c.id === 'ImageSelector')
              );
              
              if (imageSelectorComponent && typeof imageSelectorComponent === 'object') {
                if (!imageSelectorComponent.props) {
                  imageSelectorComponent.props = {};
                }
                imageSelectorComponent.props.preSelectedImages = imageUrls.map(url => ({ url }));
                
                // Set multiple pets metadata if exists
                if (metadata.multiplePets || metadata.multiplePetsList?.length > 0 || metadata.portraitStyle !== undefined) {
                  imageSelectorComponent.props.preSelectedMultiplePets = metadata;
                  console.log(`    ✅ Set preSelectedMultiplePets prop:`, metadata);
                }
                
                if (outfitMetadata && (outfitMetadata.selectedOutfitName || outfitMetadata.selectedOutfitStyle)) {
                  imageSelectorComponent.props.preSelectedOutfit = {
                    selectedOutfitName: outfitMetadata.selectedOutfitName || null,
                    selectedOutfitStyle: outfitMetadata.selectedOutfitStyle || null,
                    portraitStyle: outfitMetadata.portraitStyle ?? null
                  };
                }
                
                console.log(`    ✅ Set preSelectedImages prop on ImageSelector with ${imageUrls.length} images`);
              }
              
              if (product.currentComponent && product.currentComponent.id === 'ImageSelector') {
                if (!product.currentComponent.emits) {
                  product.currentComponent.emits = {};
                }
                product.currentComponent.emits.selection = {
                  selectedImages: imageUrls.map(url => ({ url })),
                  ...metadata,
                  ...(outfitMetadata || {})
                };
                console.log(`    ✅ Set emits.selection on current ImageSelector`);
              }
            }
            break;
            
          case 'socialMedia':
            // Restore social media selection (even if username is empty)
            if (entry.input) {
              const username = entry.input.username || '';
              const platform = entry.input.platform || '';
              console.log(`  - Restoring social media: ${platform} - @${username || '(empty)'}`);
              
              const imageSelectorComponent = product.components?.find(c => 
                (typeof c === 'string' && c === 'ImageSelector') || 
                (c && c.id === 'ImageSelector')
              );
              
              if (imageSelectorComponent && typeof imageSelectorComponent === 'object') {
                if (!imageSelectorComponent.props) {
                  imageSelectorComponent.props = {};
                }
                // Set social media prop (even if empty)
                imageSelectorComponent.props.preSelectedSocialMedia = {
                  platform: entry.input.platform || null,
                  username: entry.input.username || null,
                  multiplePets: entry.input.multiplePets || false,
                  multiplePetsList: entry.input.multiplePetsList || [],
                  portraitStyle: entry.input.portraitStyle || false
                };
                if (entry.input.selectedOutfitName || entry.input.selectedOutfitStyle) {
                  imageSelectorComponent.props.preSelectedOutfit = {
                    selectedOutfitName: entry.input.selectedOutfitName || null,
                    selectedOutfitStyle: entry.input.selectedOutfitStyle || null,
                    portraitStyle: entry.input.portraitStyle ?? null
                  };
                }
                console.log(`    ✅ Set preSelectedSocialMedia prop on ImageSelector`);
              }
              
              if (product.currentComponent && product.currentComponent.id === 'ImageSelector') {
                if (!product.currentComponent.emits) {
                  product.currentComponent.emits = {};
                }
                product.currentComponent.emits.selection = {
                  type: 'social',
                  platform: entry.input.platform,
                  username: entry.input.username,
                  multiplePets: entry.input.multiplePets,
                  multiplePetsList: entry.input.multiplePetsList,
                  portraitStyle: entry.input.portraitStyle
                };
                console.log(`    ✅ Set social media selection on current ImageSelector`);
              }
            }
            break;
            
          case 'text':
            // Restore text - entry.input is string
            if (entry.input) {
              console.log(`  - Restoring text: "${entry.input}"`);
              
              // Find the QuoteInputor component
              const quoteInputorComponent = product.components?.find(c => 
                (typeof c === 'string' && c === 'QuoteInputor') || 
                (c && c.id === 'QuoteInputor')
              );
              
              if (quoteInputorComponent && typeof quoteInputorComponent === 'object') {
                if (!quoteInputorComponent.props) {
                  quoteInputorComponent.props = {};
                }
                quoteInputorComponent.props.preSelectedText = entry.input;
                console.log(`    ✅ Set preSelectedText prop on QuoteInputor`);
              }
              
              // Also set on currentComponent if it's the QuoteInputor
              if (product.currentComponent && product.currentComponent.id === 'QuoteInputor') {
                if (!product.currentComponent.emits) {
                  product.currentComponent.emits = {};
                }
                product.currentComponent.emits.selection = {
                  text: entry.input
                };
                console.log(`    ✅ Set emits.selection on current QuoteInputor`);
              }
            }
            break;
            
          case 'color':
            // Restore color - entry.input is string (color code)
            if (entry.input) {
              console.log(`  - Restoring color: ${entry.input}`);
              
              // Find the ColorSelector component
              const colorSelectorComponent = product.components?.find(c => 
                (typeof c === 'string' && (c === 'ColorSelector' || c === 'ColorSpectrumSelector')) || 
                (c && (c.id === 'ColorSelector' || c.id === 'ColorSpectrumSelector'))
              );
              
              if (colorSelectorComponent && typeof colorSelectorComponent === 'object') {
                if (!colorSelectorComponent.props) {
                  colorSelectorComponent.props = {};
                }
                colorSelectorComponent.props.preSelectedColor = entry.input;
                console.log(`    ✅ Set preSelectedColor prop on ColorSelector`);
              }
              
              // Also set on currentComponent if it's a color selector
              if (product.currentComponent && (product.currentComponent.id === 'ColorSelector' || product.currentComponent.id === 'ColorSpectrumSelector')) {
                if (!product.currentComponent.emits) {
                  product.currentComponent.emits = {};
                }
                product.currentComponent.emits.selection = {
                  color: entry.input
                };
                console.log(`    ✅ Set emits.selection on current ColorSelector`);
              }
            }
            break;
            
          case 'phoneModel':
            // Restore phone model - entry.input is object {brand, model}
            if (entry.input && entry.input.brand && entry.input.model) {
              console.log(`  - Restoring phone model: ${entry.input.brand} ${entry.input.model}`);
              
              // Find the PhoneCaseModelSelector component
              const phoneModelComponent = product.components?.find(c => 
                (typeof c === 'string' && c === 'PhoneCaseModelSelector') || 
                (c && c.id === 'PhoneCaseModelSelector')
              );
              
              if (phoneModelComponent && typeof phoneModelComponent === 'object') {
                if (!phoneModelComponent.props) {
                  phoneModelComponent.props = {};
                }
                phoneModelComponent.props.preSelectedModel = entry.input;
                console.log(`    ✅ Set preSelectedModel prop on PhoneCaseModelSelector`);
              }
              
              // Also set on currentComponent if it's the PhoneCaseModelSelector
              if (product.currentComponent && product.currentComponent.id === 'PhoneCaseModelSelector') {
                if (!product.currentComponent.emits) {
                  product.currentComponent.emits = {};
                }
                product.currentComponent.emits.selection = entry.input;
                console.log(`    ✅ Set emits.selection on current PhoneCaseModelSelector`);
              }
            }
            break;
            
          default:
            console.log(`  - Restoring ${entry.type}:`, entry.input);
        }
      });
      
      console.log('✅ Restored all personalization for:', product.name);
    }
  }
};
</script>

<style scoped>
.progress-buttons {
  display: flex;
  justify-content: space-around;
  /* flex-wrap: wrap;
  gap: 1rem; */
  margin-top: 1rem;
}

.progress-button {
  padding: 0.75rem 1.5rem;
  border: none;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
  font-size: 1rem;
}

.progress-button.grey {
  background-color: grey;
}

.progress-button.yellow {
  background-color: yellow;
  color: #000;
}

.cancel-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #ff4d4f;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.under-development {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.development-message {
  text-align: center;
  max-width: 600px;
  background: #fffef9;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.development-message h3 {
  font-size: 24px;
  color: #e37267;
  margin-bottom: 16px;
  font-weight: 600;
}

.development-message p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 24px;
}

.back-button {
  padding: 12px 32px;
  background-color: #e37267;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 2px 3px 2px 0px rgba(168, 98, 91, 0.9);
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #d45d52;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(227, 114, 103, 0.4);
}
.end-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #52c41a;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.main-page {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80vh;
  width: auto;
  scroll-margin-top: 20px; /* Adjust this value as needed */
  position: relative; /* Add position relative to contain the hint */
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.help-button {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #e37267;
  color: white;
  border: none;
  font-size: 32px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  z-index: 9999; /* Ensure it's above everything */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  padding: 0;
  line-height: 1;
}

.help-button:hover {
  transform: scale(1.1) rotate(10deg);
  background-color: #d45d52;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.help-button:active {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .help-button {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
}
</style>